import React, { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, X, Users, Link2 } from "lucide-react";
import { api } from "@/platform/api";
import { ReviewerLinkCopy } from "@/platform/app/ReviewerLinkCopy";
import { useAuth } from "@/platform/AuthContext";
import { Button } from "@/platform/components/primitives";
import InviteReviewerModal from "@/platform/app/InviteReviewerModal";

const ROLE_BADGE = {
  owner: "bg-[#7C6BFF22] text-[#A99BFF]",
  admin: "bg-[#4F9CFF22] text-[#7FB6FF]",
  member: "bg-[#2A3040] text-[#AAB1BF]",
};

function Avatar({ name, url }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const hue = [...(name || "x")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return url ? <img src={url} alt="" className="h-9 w-9 rounded-full object-cover" />
    : <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[13px] font-semibold text-white" style={{ background: `hsl(${hue} 45% 45%)` }}>{initial}</span>;
}

function AddMemberModal({ project, onClose, onAdded }) {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { try { const r = await api.get(`/api/projects/${project.id}/assignable-members`); setList(r.members || []); } catch (_e) { setList([]); } })(); }, [project.id]);
  const add = async () => {
    const userIds = Object.keys(sel).filter((k) => sel[k]);
    if (!userIds.length) return;
    setBusy(true);
    try { await api.post(`/api/projects/${project.id}/members`, { userIds }); onAdded?.(); onClose(); } catch (e) { alert(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="add-member-modal">
      <div className="w-full max-w-md rounded-2xl border border-[#242A38] bg-[#151A24] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold text-[#F4F5F8]">Add team member</h2>
          <button onClick={onClose} className="text-[#6E7686] hover:text-[#F4F5F8]"><X className="h-5 w-5" /></button>
        </div>
        {list === null ? <div className="grid place-items-center py-10"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>
          : list.length === 0 ? <p className="py-6 text-center text-[13.5px] text-[#98A0B0]">All eligible members are already on this project. Invite a new teammate from the Team page.</p>
            : (
              <div className="max-h-72 space-y-1 overflow-auto">
                {list.map((m) => (
                  <label key={m.userId} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-[#12151D]" data-testid={`assignable-${m.userId}`}>
                    <input type="checkbox" checked={!!sel[m.userId]} onChange={(e) => setSel((s) => ({ ...s, [m.userId]: e.target.checked }))} className="accent-[#6D5EF6]" />
                    <Avatar name={m.fullName} url={m.avatarUrl} />
                    <div className="min-w-0"><p className="truncate text-[13.5px] text-[#E6E8EE]">{m.fullName}</p><p className="truncate text-[12px] text-[#6E7686]">{m.email}</p></div>
                  </label>
                ))}
              </div>
            )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="sm:w-auto sm:px-5">Cancel</Button>
          <Button onClick={add} loading={busy} disabled={!Object.values(sel).some(Boolean)} className="sm:w-auto sm:px-5" data-testid="add-to-project-btn">Add to project</Button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectTeam({ project }) {
  const { workspace } = useAuth();
  const role = workspace?.role;
  const canManage = role === "owner" || role === "admin";
  const [members, setMembers] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showReviewer, setShowReviewer] = useState(false);

  const load = useCallback(async () => {
    try {
      const m = await api.get(`/api/projects/${project.id}/members`);
      setMembers(m.members || []);
      const r = await api.get(`/api/projects/${project.id}/reviewer-invites`);
      setReviewers((r.invites || []).filter((i) => i.status === "active"));
    } catch (_e) { setMembers([]); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const removeMember = async (uid) => {
    try { await api.del(`/api/projects/${project.id}/members/${uid}`); load(); } catch (e) { alert(e.message); }
  };

  const internalCount = members?.length || 0;

  return (
    <div className="space-y-8" data-testid="project-team">
      {/* Internal team */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-[15px] font-semibold text-[#F4F5F8]">Internal team</h3>
            <p className="text-[12.5px] text-[#6E7686]">{internalCount} team member{internalCount === 1 ? "" : "s"} with access to this project</p>
          </div>
          {canManage && <Button variant="outline" onClick={() => setShowAdd(true)} className="sm:w-auto sm:px-4" data-testid="add-member-btn"><UserPlus className="h-4 w-4" /> Add team member</Button>}
        </div>
        {members === null ? (
          <div className="grid place-items-center py-12"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>
        ) : internalCount <= 1 ? (
          <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-10 text-center" data-testid="project-team-empty">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><Users className="h-6 w-6" style={{ color: "#A99BFF" }} /></div>
            <p className="mt-3 text-[15px] font-semibold text-[#F4F5F8]">Add your project team</p>
            <p className="mt-1 text-[13px] text-[#98A0B0]">Give teammates access to this project.</p>
            {canManage && <div className="mt-4 flex justify-center"><Button onClick={() => setShowAdd(true)} className="sm:w-auto sm:px-5"><UserPlus className="h-4 w-4" /> Add team member</Button></div>}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="project-internal-team">
            {members.map((m) => (
              <div key={m.userId} className="flex items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 last:border-b-0" data-testid={`project-member-${m.userId}`}>
                <Avatar name={m.fullName} url={m.avatarUrl} />
                <div className="min-w-0 flex-1"><p className="truncate text-[14px] font-medium text-[#E6E8EE]">{m.fullName}</p><p className="truncate text-[12.5px] text-[#6E7686]">{m.email}</p></div>
                <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-semibold capitalize ${ROLE_BADGE[m.role]}`}>{m.role}</span>
                {canManage && !m.implicit && (
                  <button onClick={() => removeMember(m.userId)} title="Remove from project" className="text-[#6E7686] hover:text-[#F87171]" data-testid={`project-remove-${m.userId}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reviewers */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-[15px] font-semibold text-[#F4F5F8]">Reviewers</h3>
            <p className="text-[12.5px] text-[#6E7686]">{reviewers.length} external reviewer link{reviewers.length === 1 ? "" : "s"} · accountless client access</p>
          </div>
          {canManage && <Button variant="outline" onClick={() => setShowReviewer(true)} className="sm:w-auto sm:px-4" data-testid="project-invite-reviewer-btn"><Link2 className="h-4 w-4" /> Invite reviewer</Button>}
        </div>
        {reviewers.length === 0 ? (
          <div className="rounded-2xl border border-[#242A38] bg-[#12151D] px-4 py-6 text-center text-[13px] text-[#6E7686]" data-testid="project-reviewers-empty">No active reviewer links yet.</div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="project-reviewers">
            {reviewers.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 last:border-b-0" data-testid={`project-reviewer-${inv.id}`}>
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[#00C2E022]"><Link2 className="h-4 w-4 text-[#00C2E0]" /></span>
                <div className="min-w-0 flex-1"><p className="truncate text-[14px] font-medium text-[#E6E8EE]">{inv.label}</p><p className="text-[12px] text-[#6E7686]">{inv.lastUsedAt ? "Active · used" : "Active · never opened"}{inv.expiresAt ? "" : " · no expiration"}</p></div>
                <ReviewerLinkCopy projectId={project.id} inv={inv} testid={`project-reviewer-copy-${inv.id}`} />
              </div>
            ))}
          </div>
        )}
        {!canManage && <p className="mt-2 text-[12px] text-[#565E70]">Only workspace owners and admins can manage reviewer access.</p>}
      </section>

      {showAdd && <AddMemberModal project={project} onClose={() => setShowAdd(false)} onAdded={load} />}
      {showReviewer && <InviteReviewerModal project={project} onClose={() => setShowReviewer(false)} onChanged={load} />}
    </div>
  );
}
